/**
 * Date-range filter for the Token Usage tab. Mirrors the signups DateRangeBar's chip
 * conventions, minus the "All time" preset — the U6 endpoints cap a window at 90 days,
 * so an all-time chip would be a button that always 400s.
 *
 * The custom end date is INCLUSIVE here; tokenUsageRange converts it to the exclusive
 * form the wire takes. An invalid range surfaces the inline error and sends no request.
 */
import {
  TOKEN_USAGE_RANGE_LABELS,
  type TokenUsageRange,
  type TokenUsageRangePreset,
} from './tokenUsageRange';

export interface TokenUsageRangeBarProps {
  value: TokenUsageRange;
  onChange: (next: TokenUsageRange) => void;
  error: string | null;
}

const PRESETS: TokenUsageRangePreset[] = ['7d', '30d', '90d', 'custom'];

export function TokenUsageRangeBar({ value, onChange, error }: TokenUsageRangeBarProps) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {PRESETS.map(preset => {
        const active = value.preset === preset;
        return (
          <button
            key={preset}
            type="button"
            aria-pressed={active}
            onClick={() =>
              onChange(preset === 'custom' ? { ...value, preset } : { preset })
            }
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              active
                ? 'bg-primary text-primary-foreground border-accent-text'
                : 'bg-card text-text-secondary border-border hover:bg-muted'
            }`}
          >
            {TOKEN_USAGE_RANGE_LABELS[preset]}
          </button>
        );
      })}

      {value.preset === 'custom' && (
        <span className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground">
            Start
            <input
              type="date"
              value={value.customStart ?? ''}
              onChange={e => onChange({ ...value, customStart: e.target.value })}
              className="ml-2 px-2 py-1 text-sm border border-border rounded bg-card text-foreground"
            />
          </label>
          <label className="text-sm text-muted-foreground">
            End <span className="text-xs">(end date inclusive)</span>
            <input
              type="date"
              value={value.customEnd ?? ''}
              onChange={e => onChange({ ...value, customEnd: e.target.value })}
              className="ml-2 px-2 py-1 text-sm border border-border rounded bg-card text-foreground"
            />
          </label>
        </span>
      )}

      <span className="text-xs text-muted-foreground">
        Max 90 days. All dates UTC.
      </span>

      {error && (
        <span role="alert" className="text-sm text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}
